import { IframeMessage } from '@/constants/iframeMessages';
import type { ProductItem } from '@/types/order';
import { logError } from '@/utils/logger';
interface ErrorData {
  isFatal?: boolean;
  reason?: string;
  /**
   * Stable gateway failure code (`CheckoutSessionErrorCode`) when the BE sent
   * one. Lets publishers branch on the cause — expired vs. already-used vs.
   * bad token — without matching on `reason`, which is free text.
   */
  code?: string;
}
interface PromoCodeData {
  name: string;
  discountAmount: number;
  discountPercentage: number;
}
interface EventData {
  orderId?: string;
  date?: string;
  sessionId?: string;
  bundleName?: string;
  bundleId?: string;
  bundleSKU?: string;
  products: ProductItem[];
  totalSum?: number;
  totalSumCurrency?: string;
  paymentMethodName?: string;
  previousPaymentMethodName?: string;
  userId?: string;
  userCountry?: string;
  reason?: string;
  isSavedPaymentMethodUsed?: boolean;
  isNewPaymentMethodSaved?: boolean;
  error?: ErrorData;
  promoCode: PromoCodeData | null;
  /** Subscription checkout only. */
  subscriptionId?: string;
}
interface DimensionsData {
  /** Top of the checkout to the bottom of the Footer, in px. */
  height: number;
}
interface MessageConfig {
  targetOrigin?: string;
  targetWindow?: Window;
}

class IframeMessenger {
  private targetOrigin: string;
  private targetWindow: Window;

  constructor(config?: MessageConfig) {
    this.targetOrigin = config?.targetOrigin ?? '*';
    this.targetWindow = config?.targetWindow ?? window.parent;
  }

  setTargetOrigin(origin: string): void {
    this.targetOrigin = origin;
  }

  setTargetWindow(target: Window): void {
    this.targetWindow = target;
  }

  private emit(event: IframeMessage, params: EventData | DimensionsData): void {
    try {
      this.targetWindow.postMessage({ event, params }, this.targetOrigin);
    } catch (error) {
      logError('Failed to post message', error);
      throw error;
    }
  }

  postMessage(event: IframeMessage, eventData: EventData): void {
    this.emit(event, eventData);
  }

  postDimensions(dimensions: DimensionsData): void {
    try {
      this.emit(IframeMessage.CHECKOUT_DIMENSIONS_CHANGED, dimensions);
    } catch (error) {
      logError('Failed to post dimensions message', error);
    }
  }

  postMessageSafe(eventData: EventData): boolean {
    try {
      this.targetWindow.postMessage(eventData, this.targetOrigin);
      return true;
    } catch (error) {
      logError('Failed to post message', error);
      return false;
    }
  }
}

export const iframeMessenger = new IframeMessenger();

export { IframeMessenger };
export type { EventData, MessageConfig, ErrorData, PromoCodeData, DimensionsData };
